import React, { useRef } from "react";
import { Chip } from "components/common/Chip";
import { Tooltip } from "@/components/common/Tooltip";
import { formatQkindLabel } from "./formatQkindLabel";

export interface QkindChip {
  uri: string;
  label: string;
}

interface QkindChipStripProps {
  /** All chips to render (server-suggested, neighbors, current.kind, etc.).
   *  Already deduped by URI by the parent. */
  chips: QkindChip[];
  /** The currently-pinned active filter, or `null` for no filter. */
  activeUri: string | null;
  onToggle: (uri: string) => void;
}

/**
 * Horizontal-scrolling chip strip. State machine (see plan):
 *
 * - Hidden when `chips` is empty (no search, no pin, no heuristic, no current).
 * - Idle + heuristic / Idle + current / Typing: parent supplies the chip set;
 *   strip just renders them with no special pin treatment.
 * - Pin active: the active chip moves to leftmost slot followed by a hairline
 *   divider; remaining chips render after the divider.
 *
 * Pin × is wired via `Chip`'s `onClose`. `←/→` cycles focus across chips
 * (`role="toolbar"`).
 */
export function QkindChipStrip({
  chips,
  activeUri,
  onToggle,
}: QkindChipStripProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  if (chips.length === 0) return null;

  // Reorder: active first, then everything else in input order.
  const activeIdx = activeUri
    ? chips.findIndex((c) => c.uri === activeUri)
    : -1;
  const ordered =
    activeIdx >= 0
      ? [chips[activeIdx], ...chips.filter((_, i) => i !== activeIdx)]
      : chips;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    const target = e.target as HTMLElement;
    const buttons = Array.from(
      containerRef.current?.querySelectorAll<HTMLElement>(
        '[data-qkind-chip="1"]',
      ) ?? [],
    );
    const idx = buttons.indexOf(target);
    if (idx === -1) return;
    e.preventDefault();
    const next = e.key === "ArrowRight" ? idx + 1 : idx - 1;
    const wrapped = (next + buttons.length) % buttons.length;
    buttons[wrapped]?.focus();
  };

  // Strip sizing is anchored to control-height-md (the same height as
  // unit rows + form inputs in the picker) plus equal padding on top
  // and bottom. We commit to an explicit min-height so the strip never
  // collapses to text-height: with `overflow-x: auto` the browser also
  // clips on the cross-axis (CSS spec — `overflow-y` computes to `auto`
  // when `overflow-x` is non-visible), and a naturally-sized strip will
  // clip the chip's padding + border the moment any parent flex
  // pressure shaves a pixel off. `flex-shrink: 0` defends against that
  // pressure; `box-sizing: border-box` keeps padding inside the
  // declared min-height.
  return (
    <div
      ref={containerRef}
      role="toolbar"
      aria-label="Filter by quantity kind"
      onKeyDown={handleKeyDown}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--spacing-xs)",
        overflowX: "auto",
        overflowY: "visible",
        paddingTop: "var(--spacing-xs)",
        paddingBottom: "var(--spacing-xs)",
        paddingLeft: "var(--spacing-2xs)",
        paddingRight: "var(--spacing-2xs)",
        minHeight: "calc(var(--control-height-md) + 2 * var(--spacing-xs))",
        flexShrink: 0,
        boxSizing: "border-box",
        scrollbarWidth: "thin",
      }}
    >
      {ordered.map((chip, idx) => {
        const isActive = chip.uri === activeUri;
        const isPin = isActive && activeIdx >= 0;
        const showDividerAfter = isPin && idx === 0 && ordered.length > 1;
        const displayLabel = formatQkindLabel(chip.label);
        return (
          <React.Fragment key={chip.uri}>
            <Tooltip title={displayLabel}>
              <span
                data-qkind-chip="1"
                style={{
                  display: "inline-flex",
                  flexShrink: 0,
                  maxWidth: "14rem",
                }}
              >
                <Chip
                  label={displayLabel}
                  active={isActive}
                  variant={isActive ? "info" : "default"}
                  onClick={() => onToggle(chip.uri)}
                  onClose={isPin ? () => onToggle(chip.uri) : undefined}
                  ariaLabel={`${isActive ? "Remove filter" : "Filter by"} ${displayLabel}`}
                />
              </span>
            </Tooltip>
            {showDividerAfter && (
              <span
                aria-hidden
                style={{
                  width: "var(--size-border-hairline)",
                  alignSelf: "stretch",
                  flexShrink: 0,
                  background: "var(--color-border)",
                  marginLeft: "var(--spacing-2xs)",
                  marginRight: "var(--spacing-2xs)",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
